import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of, tap } from 'rxjs';
import {
  CONSUMER_PRESET_TARGET_TYPE,
  getConsumerDefaultPresets,
} from '../filters/consumer-default-presets';

export interface FilterPreset {
  id: string;
  name: string;
  description?: string;
  targetType: string;
  presetType: 'SYSTEM' | 'USER';
  filterData: any;
  isPublic?: boolean;
  createdBy?: string;
  createdAt?: number;
  updatedAt?: number;
}

/**
 * Built-in presets a surface ships with, so a book offers useful saved filters
 * before anyone has saved one. They are surfaced as SYSTEM presets, exactly
 * like the ones the server can hand back, and are never editable or deletable.
 */
const DEFAULT_PRESET_PROVIDERS: Record<
  string,
  () => Omit<FilterPreset, 'targetType' | 'presetType'>[]
> = {
  [CONSUMER_PRESET_TARGET_TYPE]: getConsumerDefaultPresets,
};

/**
 * Shared Filter Preset Service
 * Manages filter presets across all products (policy, dialer, commissions, etc.)
 * Presets are stored in Firestore via server API
 *
 * NOTE: This service requires API_BASE_URL to be provided via injection token
 */
@Injectable({
  providedIn: 'root',
})
export class FilterPresetService {
  private readonly http = inject(HttpClient);

  // API URL will be set by the consuming app
  private apiUrl = '';

  /**
   * Set the API base URL (call this from app initializer)
   */
  setApiUrl(baseUrl: string): void {
    this.apiUrl = `${baseUrl}/v1/filter-presets`;
  }

  // State
  presets = signal<FilterPreset[]>([]);
  loading = signal(false);
  activePresetId = signal<string | undefined>(undefined);

  /**
   * Built-in presets for a target type, as SYSTEM presets
   */
  defaultPresets(targetType: string): FilterPreset[] {
    const provider = DEFAULT_PRESET_PROVIDERS[targetType];
    if (!provider) {
      return [];
    }
    return provider().map(preset => ({
      ...preset,
      targetType,
      presetType: 'SYSTEM' as const,
      isPublic: true,
      createdBy: 'system',
    }));
  }

  /**
   * Built-ins first, then anything the server returned that is not a built-in
   */
  private withDefaults(
    targetType: string,
    presets: FilterPreset[],
    includeSystem = true
  ): FilterPreset[] {
    if (!includeSystem) return presets;
    const defaults = this.defaultPresets(targetType);
    if (defaults.length === 0) {
      return presets;
    }
    const defaultIds = new Set(defaults.map(preset => preset.id));
    return [...defaults, ...presets.filter(p => !defaultIds.has(p.id))];
  }

  /**
   * Load presets for a target type
   */
  loadPresets(
    targetType: string,
    includePublic = true,
    includeSystem = true
  ): Observable<FilterPreset[]> {
    this.loading.set(true);
    // Show the built-ins straight away rather than after the round trip.
    this.presets.set(this.withDefaults(targetType, [], includeSystem));

    return this.http
      .get<{ presets: FilterPreset[]; total: number }>(this.apiUrl, {
        params: {
          targetType,
          includePublic: String(includePublic),
          includeSystem: String(includeSystem),
        },
      })
      .pipe(
        map(response =>
          this.withDefaults(targetType, response.presets, includeSystem)
        ),
        tap(presets => {
          this.presets.set(presets);
          this.loading.set(false);
        }),
        catchError(error => {
          console.error('Error loading presets', error);
          this.presets.set(this.withDefaults(targetType, [], includeSystem));
          this.loading.set(false);
          return of(this.presets());
        })
      );
  }

  /**
   * Save current filter as a preset
   */
  savePreset(
    name: string,
    targetType: string,
    filterData: any,
    description?: string,
    isPublic = false
  ): Observable<FilterPreset | null> {
    return this.http
      .post<{ success: boolean; preset: FilterPreset }>(this.apiUrl, {
        name,
        description,
        targetType,
        filterData,
        isPublic,
      })
      .pipe(
        map(response => response.preset),
        tap(preset => {
          if (preset) {
            this.presets.update(presets => [...presets, preset]);
          }
        }),
        catchError(error => {
          console.error('Error saving preset', error);
          return of(null);
        })
      );
  }

  /**
   * Update an existing preset
   */
  updatePreset(
    presetId: string,
    updates: {
      name?: string;
      description?: string;
      filterData?: any;
      isPublic?: boolean;
    }
  ): Observable<FilterPreset | null> {
    return this.http
      .patch<{
        success: boolean;
        preset: FilterPreset;
      }>(`${this.apiUrl}/${presetId}`, updates)
      .pipe(
        map(response => response.preset),
        tap(preset => {
          if (preset) {
            this.presets.update(presets =>
              presets.map(p => (p.id === presetId ? preset : p))
            );
          }
        }),
        catchError(error => {
          console.error('Error updating preset', error);
          return of(null);
        })
      );
  }

  /**
   * Delete a preset
   */
  deletePreset(presetId: string): Observable<boolean> {
    return this.http
      .delete<{ success: boolean }>(`${this.apiUrl}/${presetId}`)
      .pipe(
        map(response => response.success),
        tap(success => {
          if (success) {
            this.presets.update(presets =>
              presets.filter(p => p.id !== presetId)
            );
            if (this.activePresetId() === presetId) {
              this.activePresetId.set(undefined);
            }
          }
        }),
        catchError(error => {
          console.error('Error deleting preset', error);
          return of(false);
        })
      );
  }

  /**
   * Apply a preset
   */
  applyPreset(preset: FilterPreset): void {
    this.activePresetId.set(preset.id);
  }

  /**
   * Clear active preset
   */
  clearPreset(): void {
    this.activePresetId.set(undefined);
  }
}
